import { Box } from '@mui/material';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../core-components/theme/components/Buttons';
import { InlineAlert } from '../../../core-components/theme/components/InlineAlert';
import { StickyActionBar } from '../../../core-components/theme/components/StickyActionBar';
import { useFocusOnMount } from '../../../utilities/customHooks/useFocusOnMount';
import type { AlertChannel, PasscodeChannel } from '../types/domain.types';

import { AuthPanelHeading, AuthSubtitle, authContentSx, authPanelSx } from './AuthPanel';
import { IdentifierField } from './IdentifierField';
import type { IdentifierFieldState } from './IdentifyPanel';
import { OtherChannelSendButton, PrimarySendButton } from './SendCodeButtons';
import type { SendState } from './SendCodeButtons';

export interface NoPasswordPanelProps {
  readonly title: string;
  readonly message: string;
  readonly subtitle?: React.ReactNode;
  readonly field: IdentifierFieldState;
  readonly fieldProblemKey: number;
  readonly codeChoices: readonly AlertChannel[];
  readonly sendingChannel: PasscodeChannel | null;
  readonly sending: boolean;
  readonly held: boolean;
  readonly notice?: React.ReactNode;
  readonly onSend: (channel?: AlertChannel) => void;
  readonly onBack: () => void;
}

export const NoPasswordPanel: React.FC<NoPasswordPanelProps> = ({
  title,
  message,
  subtitle,
  field,
  fieldProblemKey,
  codeChoices,
  sendingChannel,
  sending,
  held,
  notice,
  onSend,
  onBack,
}) => {
  const { t } = useTranslation();
  const subtitleId = useId();
  const messageId = useId();
  const noticeId = useId();
  const inputRef = useFocusOnMount<HTMLInputElement>(fieldProblemKey > 0, fieldProblemKey);
  const hasSubtitle = subtitle !== undefined && subtitle !== null;
  const [firstChoice, otherChoice] = codeChoices;
  const sendState: SendState = { sendingChannel, sending, unavailable: held, describedBy: held ? `${messageId} ${noticeId}` : messageId };
  const sendFirst = (): void => onSend(firstChoice);
  const back = (
    <Button type="button" intent={otherChoice === undefined ? 'secondary' : 'tertiary'} aria-disabled={sending || undefined} onClick={onBack}>
      {t('passcodeAuth.login.back', 'Back')}
    </Button>
  );
  const otherChannel = otherChoice === undefined ? undefined : <OtherChannelSendButton channel={otherChoice} onSend={onSend} {...sendState} />;

  return (
    <Box
      component="form"
      noValidate
      sx={authPanelSx}
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        sendFirst();
      }}
    >
      <Box sx={authContentSx}>
        <AuthPanelHeading title={title}>{hasSubtitle && <AuthSubtitle id={subtitleId}>{subtitle}</AuthSubtitle>}</AuthPanelHeading>
        <IdentifierField {...field} inputRef={inputRef} describedById={hasSubtitle ? subtitleId : undefined} onEnter={sendFirst} />
        <InlineAlert id={messageId} severity="info" role="status" message={message} />
        <Box id={noticeId}>{notice}</Box>
      </Box>
      <StickyActionBar
        layout="dock"
        announce={false}
        tertiary={otherChannel === undefined ? undefined : back}
        secondary={otherChannel ?? back}
        primary={<PrimarySendButton channel={firstChoice} autoFocus {...sendState} />}
      />
    </Box>
  );
};
